import {vec3, vec4} from 'gl-matrix';
import {decode as decodeCBOR} from 'cbor-x';

export const assert = console.assert;

export const lerp = (a, b, u) => a + u*(b - a);
export const clamp = (x, a, b) => x < a ? a : b < x ? b : x;
export const smoothstep = (a, b, x) => {
    const u = (x - a) / (b - a);
    return u<0 ? 0 : u>1 ? 1 : u*u*(3 - 2*u);
};

export function createElement(tagAndNames, props) {
    const [tag, ...names] = tagAndNames.split('.');
    const el = document.createElement(tag || 'div');
    el.className = names.join(' ');
    Object.assign(el, props);
    if (typeof props.style === 'object') {
        for (let [k, v] of Object.entries(props.style))
            el.style[k] = v;
    }
    else if (typeof props.style === 'string') {
        el.style.cssText = props.style;
    }
    return el;
}

export function boxInit(box) {
    return vec4.set(box || vec4.create(),
        Infinity, Infinity,
        -Infinity, -Infinity,
    );
}

export function boxAddPoint(box, x, y) {
    box[0] = Math.min(box[0], x);
    box[1] = Math.min(box[1], y);
    box[2] = Math.max(box[2], x);
    box[3] = Math.max(box[3], y);
}

export function boxAddBox(box, o) {
    boxAddPoint(box, o[0], o[1]);
    boxAddPoint(box, o[2], o[1]);
    boxAddPoint(box, o[2], o[3]);
    boxAddPoint(box, o[0], o[3]);
}

export function boxIsEmpty(box) {
    return !(box[0] < box[2] && box[1] < box[3]);
}

export function parseHexColor(out, s) {
    assert(s.length === 7 && s[0] === '#');
    out[0] = parseInt(s.slice(1, 3), 16);
    out[1] = parseInt(s.slice(3, 5), 16);
    out[2] = parseInt(s.slice(5, 7), 16);
    vec3.scale(out, out, 1/255);
    if (out.length === 4)
        out[3] = 1;
    return out;
}

export function range(...args) {
    let lo, hi, step;

    if (args.length === 1) {
        lo = 0;
        hi = args[0];
        step = 1;
    }
    else if (args.length === 2) {
        lo = args[0];
        hi = args[1];
        step = 1;
    }
    else if (args.length === 3) {
        lo = args[0];
        hi = args[1];
        step = args[2];
    }

    const out = [];
    for (let i = lo; i < hi; i += step)
        out.push(i);

    return out;
}

export const loadImage = async src => {
    const image = Object.assign(new Image, {src});
    await image.decode();
    return image;
};

// https://gist.github.com/iperelivskiy/4110988?permalink_comment_id=3716618#gistcomment-3716618
export function hashString(s) {
    let h = 9;
    let k = (9 ** 9)|0;
    let i = s.length - 1;
    while (i >= 0) {
        h = Math.imul(h ^ s.charCodeAt(i--), k);
    }
    return h ^ (h >>> 9);
}

export const fetchCBOR = url => (
    fetch(url)
        .then(r => r.arrayBuffer())
        .then(ab => decodeCBOR(new Uint8Array(ab))));

export const timeNow = () => performance.now();
export const expovariate = mu => -Math.log(1 - Math.random()) * mu;

export function shuffle(array, random=Math.random) {
    var m = array.length, t, i;

    // While there remain elements to shuffle…
    while (m) {
        // Pick a remaining element…
        i = Math.floor(random() * m--);

        // And swap it with the current element.
        t = array[m];
        array[m] = array[i];
        array[i] = t;
    }

    return array;
}

export function fireEvent(target, type, detail) {
    const event = new CustomEvent(type, {detail});
    target.dispatchEvent(event);
}

// https://stackoverflow.com/questions/10420352/converting-file-size-in-bytes-to-human-readable-string
export function humanFileSize(bytes, si=false, dp=1) {
    const thresh = si ? 1000 : 1024;

    if (Math.abs(bytes) < thresh) {
        return bytes + ' B';
    }

    const units = si 
        ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] 
        : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
    let u = -1;
    const r = 10**dp;

    do {
        bytes /= thresh;
        ++u;
    } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

    return bytes.toFixed(dp) + ' ' + units[u];
}
